import { isNil, isString, mapValues, toNumber } from 'lodash-es';
import dayjs, { Dayjs } from 'lib/dayjs';
import { formatNumber } from 'utils/numberFormat';
import { regionFixedExchangeRate } from 'services/config/region';
import { DashboardFilterType } from './components/Filter/useFilter';

export const RMBSymbol = '￥';

export const today = () => dayjs().startOf('day');
export const yesterday = () => today().subtract(1, 'day');
/**
 * 仅适用于修改时间筛选器
 * @returns [7天前, t - 1数据更新时间]
 */
export const last7days = (): [Dayjs, Dayjs] => [dayjs(yesterday()).subtract(6, 'day'), yesterday()];
export const last15days = (): [Dayjs, Dayjs] => [
  dayjs(yesterday()).subtract(14, 'day'),
  dayjs(yesterday()),
];

export const last30days = (): [Dayjs, Dayjs] => [
  dayjs(yesterday()).subtract(29, 'day'),
  dayjs(yesterday()),
];

export const last90days = (): [Dayjs, Dayjs] => [
  dayjs(yesterday()).subtract(89, 'day'),
  dayjs(yesterday()),
];

export const momentDateRange2FetchParams = (dateRange?: [Dayjs, Dayjs]) => {
  if (!dateRange) {
    return {
      startTime: '',
      endTime: '',
    };
  }

  const [start, end] = dateRange;
  return {
    startTime: dayjs(start).format('YYYY-MM-DD'),
    endTime: dayjs(end).format('YYYY-MM-DD'),
  };
};
export const platformAll2Undefined = (platform?: General.PlatformUpperCase | 'ALL') =>
  platform === 'ALL' ? undefined : platform;

export const numDisplay = (num?: number | null) => {
  return formatNumber(num, '0,0');
};

export const numDisplayWithFixed2 = (num?: number | null) => {
  return formatNumber(num, '0,0.00');
};

export const numDisplayWithNone = (num?: number | null | string) => {
  if (isString(num)) {
    return num;
  }
  return numDisplay(num);
};

export const percentDisplayWithNone = (num?: number | null | string) => {
  if (isString(num)) {
    return num;
  }
  return percentDisplay(num ?? undefined);
};

export const numDisplayWithFixed4 = (num?: number | null) => {
  return formatNumber(num, '0.0000');
};

export const durationDisplay = (num?: number, times = 1) => {
  if (isNil(num)) {
    return '-';
  }

  const seconds = num * times;

  const duration = dayjs.duration(Math.round(seconds) * 1000);

  const hours = Math.floor(seconds / 60 / 60);

  return `${hours >= 10 ? '' : 0}${hours}:${duration.format('mm:ss')}`;
};

export const millisecondDurationDisplay = (num?: number) => durationDisplay(num, 1 / 1000);

export const secondDurationDisplay = (num?: number) => durationDisplay(num, 1);

export const minuteDurationDisplay = (num?: number) => durationDisplay(num, 60);

export const currencyDisplay = (num?: number) => {
  return formatNumber(num, '0,0.00');
};
export const getAmountByCurrency = (
  currency?: Dashboard.CurrencyShorthand,
  amount?: Dashboard.CurrencyToAmount,
): number | undefined => {
  if (!currency || currency === 'CNY') {
    return amount?.RMB?.amount;
  }
  const num = amount?.[currency]?.amount;
  if (isNil(num)) {
    return undefined;
  }
  return Number(num?.toFixed(2));
};

export const currencyDisplayWithUnit = (
  currency?: Dashboard.CurrencyShorthand,
  amount?: Dashboard.CurrencyToAmount,
) => {
  const val = getAmountByCurrency(currency, amount);
  if (!isNil(val)) {
    const target = Object.values(regionFixedExchangeRate).find((x) => x.shorthand === currency);
    return `${target?.symbol ?? RMBSymbol}${currencyDisplay(val)}`;
  }
  return '-';
};
export const percentDisplay = (num?: number, digits: number = 2) => {
  if (isNil(num) || Number.isNaN(num)) {
    return '-';
  }
  return formatNumber(num, digits === 2 ? '0.00%' : '0%');
};
export const storeType2FetchParams = (type?: IM_API.Admin.StoreType) => {
  if (type === 'CROSS') {
    return '跨境';
  }
  if (type === 'LOCAL') {
    return '本土';
  }
  return undefined;
};

export const sortByDate = <T extends { date: string; [x: string]: any }[]>(list?: T) => {
  if (!list) {
    return list;
  }
  return list.sort((a, b) => dayjs(a.date).valueOf() - dayjs(a.date).valueOf());
};

export const ctx2FetchParam = (ctx: DashboardFilterType): Dashboard.BasicParams => {
  const platform = platformAll2Undefined(ctx.platform);
  const list = ctx.storeInfo.list || [];

  return {
    ...momentDateRange2FetchParams(ctx.dateRange),
    platform,
    storeType: storeType2FetchParams(ctx.storeType),
    storeIds: list.map((x) => x.id),
  };
};
/**
 * 订单损耗默认值
 * @scene 后端不存在所选平台时
 * @param data 数据
 * @returns 篡改shopee节点的数据
 * @example Wastage.tsx :581    setEchartsDefaultValue(value)[defaultType],
 */
export const setEchartsDefaultValue = (data: Partial<Dashboard.OrderWastageInner>) => {
  const output = {
    ...data,
    shopeeCOD订单损耗情况: mapValues(data.shopeeCOD订单损耗情况, (v) =>
      mapValues(v, (_, key) => {
        if (key === '领先比率') {
          return -1;
        }
        return 0;
      }),
    ),
    shopee非COD订单损耗情况: mapValues(data.shopee非COD订单损耗情况, (v) =>
      mapValues(v, (_, key) => {
        if (key === '领先比率') {
          return -1;
        }
        return 0;
      }),
    ),
  };
  return output;
};
/**
 * 获取数据更新时间
 * @returns  [t - 2 更新时间， 今天]
 */
export const getUpdateDate = (): [Dayjs, Dayjs] => {
  const now = dayjs();
  const prev = dayjs().subtract(2, 'day');
  return [prev, now];
};

export const numberAbs = (value?: string | number) => {
  return isNil(value) ? '-' : `${Math.abs(toNumber(value))}`;
};

export const numberNegative = (value?: number) => {
  return isNil(value) ? undefined : -value;
};

export const tooltipRender = (tips?: string) =>
  tips?.split('\\n').reduce((res, cur) => {
    return `${res.slice(0, -6)}<div>${cur}</div>${res.slice(-6)}`;
  }, '<div></div>');
